import React, { useContext, useState } from 'react';
import { UndoOutlined, CloudUploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Space, Tooltip, message, Upload } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { PLAYER_COACH } from '../../../../constants/define/translation';
import { btnActive } from '../../../../styles';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { PlayerProfileType } from '../../../../types/coachProfile';
import { CONFIG_CONTAINER_NAME, deleteBlob, uploadFileToBlob } from '../../../../utils/azure';
import {
  postCreateTileWidgetRepository,
  patchUpdateTileWidgetRepository,
  deleteTileWidgetRepository,
} from '../../../../repository/mytm/shop/shopRepository';
import { SHOP_TYPE } from '../../../../types/shop';

interface ActionsColumnProps {
  record: PlayerProfileType;
  onRefresh: () => void;
  setLoading: (value: boolean) => void;
}
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const azureCDN = process.env.REACT_APP_AZURE_CDN;
export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(PLAYER_COACH);
  const { record, onRefresh, setLoading } = props;
  const userContext = useContext(UserContext);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.revert')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        const { widgetId } = record;
        if (!widgetId) {
          return;
        }
        setDeleting(true);
        const updateFunc = () => {
          deleteTileWidgetRepository(widgetId)
            .then(res => {
              deleteBlob(record?.imageLink, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              message.success(t('message.success.revertPlayer'));
            })
            .catch(() => message.error(MESSAGE_ERROR.M02))
            .finally(() => {
              onRefresh();
              setDeleting(false);
            });
        };
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        if (!userContext.user?.email || userContext.user?.email === '') {
          setLoading && setLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setDeleting(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const onHandleUpload = async (options: any) => {
    const { onError, file } = options;
    try {
      setLoading(true);
      const resultUpload = await uploadFileToBlob(
        [
          {
            blob: file,
            fileType: file.type,
            fileName: file.name,
          },
        ],
        CONFIG_CONTAINER_NAME.UPLOADS,
      );
      if (resultUpload && resultUpload.length > 0) {
        const url = resultUpload[0];
        const newUrlCDN = azureCDN ? url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN) : url;
        const payload = {
          imageLink: newUrlCDN,
          title: record?.title,
          type: SHOP_TYPE.PLAYER_PROFILE,
          options: { tourPlayerId: record?.id },
        };
        if (record.widgetId) {
          patchUpdateTileWidgetRepository({
            ...payload,
            id: record.widgetId,
          })
            .then(() => {
              deleteBlob(record?.imageLink, CONFIG_CONTAINER_NAME.UPLOADS).then().catch();
              onRefresh();
              message.success(t('message.success.uploadImageSuccess'));
              setLoading(false);
            })
            .catch(e => {
              setLoading(false);
              message.error(e?.errorMessage);
            });
        } else {
          postCreateTileWidgetRepository(payload)
            .then(() => {
              onRefresh();
              message.success(t('message.success.uploadImageSuccess'));
              setLoading(false);
            })
            .catch(e => {
              setLoading(false);
              message.error(e?.errorMessage);
            });
        }
      }
    } catch (error) {
      setLoading(false);
      onError({ error });
    }
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.tooltip.upload')}>
          <Upload
            accept="image/*"
            listType="picture"
            maxCount={1}
            showUploadList={false}
            customRequest={onHandleUpload}
          >
            <Button icon={<CloudUploadOutlined />} className="active" />
          </Upload>
        </Tooltip>
        {record.widgetId ? (
          <Tooltip title={t('table.tooltip.revert')}>
            <Button
              icon={<UndoOutlined style={{ color: '#ff4d4f' }} />}
              onClick={handleDelete}
              disabled={deleting}
              loading={deleting}
            />
          </Tooltip>
        ) : null}
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 0.5rem;
  ${btnActive}
`;
